import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ReportQAResult, runReportQA } from "../../api/reportQa";
import { CLIENT_KEYS } from "../../constants/clientKeys";

/**
 * Trigger a new Report QA pass against the user's WIP draft .docx.
 *
 * On success, primes the `useLatestReportQA` cache with the new row
 * so the panel re-renders without a follow-up GET.
 */
export const useRunReportQAMutation = ({
  templateId,
  caseNumber,
  onSuccess,
  onError,
}: {
  templateId: string;
  caseNumber: string;
  onSuccess?: (result: ReportQAResult) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<ReportQAResult, Error>({
    mutationFn: () => runReportQA({ templateId, caseNumber }),
    onSuccess: (result) => {
      queryClient.setQueryData(
        [
          CLIENT_KEYS.REPORT_GENERATOR,
          CLIENT_KEYS.TEMPLATES,
          templateId,
          CLIENT_KEYS.CASES,
          caseNumber,
          CLIENT_KEYS.REPORT_QA,
        ],
        result
      );
      onSuccess?.(result);
    },
    onError: (error) => {
      // Log unconditionally so failures are visible in the browser console
      // even when the caller didn't wire its own onError. Without this, a
      // mutation failure was previously invisible end-to-end (MDRS-92).
      // eslint-disable-next-line no-console
      console.error("Report QA mutation failed:", error);
      onError?.(error);
    },
  });
};
